import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default function StoryHero() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".story-hero-word", {
        yPercent: 100,
        opacity: 0,
        duration: 1,
        stagger: 0.07,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 75%" },
      });
      gsap.from(".story-hero-body", {
        y: 40,
        opacity: 0,
        duration: 1,
        delay: 0.3,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 75%" },
      });
      gsap.from(".story-hero-img", {
        clipPath: "inset(100% 0 0 0)",
        duration: 1.4,
        ease: "power4.out",
        scrollTrigger: { trigger: ".story-hero-img-wrap", start: "top 80%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  const line1 = "A practice of".split(" ");
  const line2 = "quiet craft.".split(" ");

  return (
    <section ref={root} className="bg-bone pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-6">
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-ink/50 mb-6">
              Our Story
            </p>
            <h1 className="font-display tracking-tightest leading-[0.95] text-ink text-4xl md:text-5xl lg:text-6xl font-light">
              <span className="block overflow-hidden">
                {line1.map((w, i) => (
                  <span key={i} className="inline-block overflow-hidden">
                    <span className="story-hero-word inline-block mr-4">{w}</span>
                  </span>
                ))}
              </span>
              <span className="block overflow-hidden font-display text-rust">
                {line2.map((w, i) => (
                  <span key={i} className="inline-block overflow-hidden">
                    <span className="story-hero-word inline-block">{w}</span>
                  </span>
                ))}
              </span>
            </h1>
            <p className="story-hero-body mt-10 text-ink/70 text-base leading-relaxed max-w-lg font-light">
              Verdant began as a single loom in a borrowed garage. We set out to prove that clothing
              could be made slowly, repaired endlessly, and returned to the earth without harm.
              Today, the same hands still guide every pattern.
            </p>
          </div>
          <div className="lg:col-span-6 story-hero-img-wrap">
            <div className="relative aspect-[4/3] rounded-[28px] overflow-hidden bg-sand">
              <div className="story-hero-img absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=artisanal%20textile%20atelier%20interior%20with%20natural%20light%20streaming%20through%20large%20windows%2C%20spools%20of%20cream%20and%20earth%20tone%20yarn%20on%20wooden%20shelves%2C%20warm%20golden%20afternoon%20light%2C%20sustainable%20fashion%20studio%20documentary%20photography%2C%20rich%20texture%20detail%2C%20serene%20craft%20atmosphere%2C%20editorial%20composition&width=1100&height=800&seq=verdant-story-hero&orientation=landscape"
                  alt="Verdant Atelier"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-bone/90 backdrop-blur text-xs font-mono uppercase tracking-[0.2em] text-ink">
                Porto, Portugal · Est. 2017
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}